import { SOCIAL_SHARE_PARAMS } from './../imports';
import type { SocialShareParams } from './../imports';
import { DEFINITION_ALIAS } from './../imports';
import { convertKeywordsListToText, generateKeywordsList } from './keyword_utils';
import { conformParamsToCharLimits, getPreciseParam } from './param_utils'
export const createOutputText = (
  inputParams: Record<string, string | null>,
  hashSymbol: boolean
): string => {
  let textOutput = '';
  if (inputParams.text) {
    textOutput += inputParams.text;
  }
  if (inputParams.via) {
    if (textOutput) textOutput += '\n';
    textOutput += inputParams.via.startsWith('@') ? inputParams.via : `@${inputParams.via}`;
  }
  if (inputParams.url) {
    if (textOutput) textOutput += '\n';
    textOutput += inputParams.url;
  }
  if (inputParams.hashtags) {
    if (textOutput) textOutput += '\n';
    textOutput += convertKeywordsListToText(inputParams.hashtags, hashSymbol);
  }
  return textOutput.trim();
};

export const createOutputUrl = (
  outputParams: Record<string, string | null>,
  shareUrl: string
): string => {
  const url = new URL(shareUrl);
  const queryParams = new URLSearchParams();
  Object.entries(outputParams).forEach(([key, value]) => {
    if (value != null && value !== '') {
      // pass raw value; URLSearchParams will encode it
      queryParams.append(key, value);
    }
  });
  url.search = queryParams.toString();
  // URLSearchParams encodes spaces as '+', which mail clients and several
  // share intents render literally; literal '+' in values is already %2B.
  return url.toString().replace(/\+/g, '%20');
};

// Builds the post body per the site convention: the page description leads,
// and the summary is appended only when the description alone falls short of
// the platform's optimal share length. Hard trimming to the limit happens
// later in conformParamsToCharLimits, after url/via costs are known.
export const composeShareText = (
  data: Record<string, string | null | undefined>,
  characters: SocialShareParams['characters']
): string => {
  const limit = characters.limit ?? Number.MAX_SAFE_INTEGER;
  const optimal = characters.optimal ?? Math.min(200, limit);
  let text = (data.text || data.description || '').trim();
  const summary = (data.summary || data.synopsis || '').trim();
  if (summary && summary !== text && text.length < optimal) {
    text = text ? `${text}\n\n${summary}` : summary;
  }
  return text;
};

// "@{sitename}" attribution when no handle is supplied: thedailydialectics
// from https://thedailydialectics.com/...
export const deriveSiteHandle = (url: string | null | undefined): string | null => {
  if (!url) return null;
  try {
    const host = new URL(url, 'https://thedailydialectics.com').hostname.replace(/^www\./, '');
    return host.split('.')[0] || null;
  } catch {
    return null;
  }
};





export const getOutputParams = (
  platform: string,
  text: string | null = null,
  url: string | null = null,
  via: string | null = null,
  hashtags: any = null,
  // Extra fields (title, description, media, …) for platforms that accept more
  // than text/url/via/hashtags — resolved by alias like everything else.
  extras: Record<string, string | null> = {}
): Record<string, string | null> => {
  const shareParams = getShareParams(platform);
  if (!shareParams) return { text: '' };

  const params = shareParams.params;
  const characters = shareParams.characters;
  const hashSymbol = shareParams.hash_symbol;

  const keywords = generateKeywordsList(hashtags);
  const inputParams: Record<string, string | null> = {
    text: composeShareText({ text, description: extras.description, summary: extras.summary }, characters),
    via: via || deriveSiteHandle(url),
    url: url || null,
    hashtags: keywords.join(',') || null,
    ...extras,
  };
  const outputParams: Record<string, string | null> = { text: '' };

  const conformedParams = conformParamsToCharLimits(inputParams, characters, hashSymbol);

  let remaining = { ...conformedParams };
  Object.entries(params).forEach(([param, paramDef]) => {
    const [picked, newRemaining] = getPreciseParam(paramDef, remaining);
    let value = picked;
    // Dedicated hashtag params (x hashtags=, tumblr tags=) take a plain
    // comma list; '#' rendering only applies when folded into post text.
    if (value && paramDef.alias.includes('hashtags')) {
      value = value.replace(/#/g, '').trim().replace(/[ ,]+/g, ',');
    }
    outputParams[param] = value;
    remaining = newRemaining;
  });

  // Whatever the intent has no dedicated param for (url, via, hashtags on
  // threads/bluesky/mailto, …) gets folded into the platform's text-bearing
  // param — 'body' for mailto, otherwise 'text'.
  const textKey = 'body' in params ? 'body' : 'text';
  const textOutput = createOutputText(remaining, hashSymbol);
  if (outputParams[textKey]) {
    const existing = (outputParams[textKey] || '').trim();
    const extra = (textOutput || '').trim();
    if (extra && existing !== extra) {
      outputParams[textKey] = `${existing}\n${extra}`.trim();
    } // else keep existing as-is
  } else {
    outputParams[textKey] = textOutput;
  }

  return outputParams;
};

export const getDataFromAlias = (data: Record<string, string | null>, alias: string): string | null => {
  const params = DEFINITION_ALIAS[alias];
  if (!params) return null;
  for (const key of params.alias) {
    const value = data[key];
    if (value) return value;
  }
  return null;
};

export const createPost = (
  platform: string,
  text: string | null = null,
  url: string | null = null,
  via: string | null = null,
  hashtags: string | string[] | Set<string> | null = null,
  outputParams: Record<string, string | null> | null = null,
  extras: Record<string, string | null> = {}
): string => {
  outputParams = outputParams || getOutputParams(platform, text, url, via, hashtags, extras);
  const shareParams = getShareParams(platform);
  if (!shareParams) return '';
  const hashSymbol = shareParams.hash_symbol;
  return createOutputText(outputParams, hashSymbol);
};

export const createPostUrl = (
  platform: string,
  text: string | null = null,
  url: string | null = null,
  via: string | null = null,
  hashtags: string | string[] | Set<string> | null = null,
  outputParams: Record<string, string | null> | null = null,
  extras: Record<string, string | null> = {}
): string => {
  outputParams = outputParams || getOutputParams(platform, text, url, via, hashtags, extras);
  const shareParams = getShareParams(platform);
  if (!shareParams) return '#';
  const shareUrl = shareParams.url;

  // Only emit params this intent actually defines — the synthetic composed
  // `text` must not leak onto url-only intents (facebook u=, minds intentUrl=).
  const allowed = new Set(Object.keys(shareParams.params));
  const filteredParams: Record<string, string | null> = {};
  Object.entries(outputParams).forEach(([key, value]) => {
    if (allowed.has(key)) filteredParams[key] = value;
  });

  return createOutputUrl(filteredParams, shareUrl);
};

export const getPostText = (data: Record<string, string | null>): string => {
  let outputText = '';
  const title = data.title || '';
  if (title) {
    outputText += title;
  }
  const pageTitle = data.pageTitle || '';
  if (pageTitle) {
    outputText += outputText ? `: ${pageTitle}` : pageTitle;
  }
  const description = data.description || '';
  if (description) {
    outputText += outputText ? `\n\n${description}` : description;
  }
  const text = getDataFromAlias(data, 'text') || '';
  if (text) {
    outputText += outputText ? `\n${text}` : text;
  }
  return outputText.trim();
};
export const getShareParams = (platform: string): SocialShareParams | undefined => {
  const config = SOCIAL_SHARE_PARAMS[platform.toLowerCase()];
  if (config) return config;
  return Object.values(SOCIAL_SHARE_PARAMS).find(params =>
    params.alias.includes(platform.toLowerCase())
  );
};

// Embedded HandleShareUrlClick (from HandleClick.tsx)
export const HandleShareUrlClick = async (
  e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  platform: string,
  text: string,
  url: string,
  via: string,
  hashtags: string,
  extras: Record<string, string | null> = {},
) => {
  e.preventDefault();
  let postData = '';
  try {
    postData = await createPostUrl(platform, text, url, via, hashtags, null, extras);
  } catch (error) {
    console.error('Error building share url:', error);
  }
  if (postData && postData !== '#') {
    window.open(postData, 'popup', 'noopener,noreferrer,width=600,height=600');
  }
};
